import React, { useState, useEffect, useContext } from 'react';
import {
    Icon,
    Col,
    Input,
    Popover,
    Select,
    Button,
    Badge,
} from 'antd';
import {
    TabMenuContainer,
    EmptySettingsDetails,
    LoaderContainer,
    TabMenuHeaderContainer,
    TabMenuHeader,
    TabMenuItemContainer,
    NoDataView,
    TabSearchContainer,
    PopoverContainer,
    ClearAll,
    NoteText,
    TemplateSingleCard
} from './styles';
import EmptyElementsIllustration from '../../images/empty-elements.svg';
import { FilterSvg } from '../SidebarMenuIcons';
import { withTranslation } from 'react-i18next';
import Loader from '../Loader';
import PageNotFoundIllustration from '../../images/generatingPreview.png';
// // app store context API
import { AppStoreContext } from '../../context/appStoreContext';
import {
    DESIGN_AREA_TEMPLATES_TAB,
} from '../../containers/SidebarMenu/translationValues';
import { getMasterlanguageList, getMasterDocument, getMasterDocumentByCategory } from '../../utils/services/complexDocumentsAPI';

const { Option } = Select;
const NAMESPACES = ['DESIGN_AREA_TEMPLATES_TAB', 'TEMPLATE_LOCKED_BY_MODAL'];

const MasterTemplate = props => {
    const {
        t,     
        setMasterTemplate
    } = props;

    const [selectedView, setSelectedView] = useState('view-templates');  
    const [fetchingCategories, setFetchingCategories] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState(undefined);
    const {
        categoryTemplates,
        updateCategoryTemplates,
        checkUserAccess,
    } = useContext(AppStoreContext);
    const [loadMore, setLoadMore] = useState(false);
    const [page, setPage] = useState(1);  
    const [defaultTemplates, setDefaultTemplates] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchedTemplates, setSearchedTemplates] = useState({});
    const [noData, setNodata] = useState(false);
    const [filterVisible, setFilterVisible] = useState(false);
    const [filterOption, setFilterOption] = useState('All');
    const [languageList, setLanguageList] = useState([]);
    const [selectedLanguages, setSelectedLangues] = useState(undefined);
    const [languageOption, setLanguageOption] = useState('All');
    const [selectedTemplate, setSelectedTemplate] = useState(false);
    const [fetchingLanguage, setFetchingLanguage] = useState(true);

    useEffect(() => {
        (async () => {
            const response = await getMasterDocumentByCategory();
            if (response && response?.data) {
                setDefaultTemplates(response.data);
                updateCategoryTemplates(response.data, 'saveAllCategoryTemplates');               
            };
            setFetchingCategories(false);

            const languageListResponse = await getMasterlanguageList();
            if (languageListResponse?.statusCode === 109 && languageListResponse?.data) {
                setLanguageList(languageListResponse.data);               
            }
            setFetchingLanguage(false);
        })();
        return () => {
          updateCategoryTemplates([], 'saveAllCategoryTemplates');
        }
        // eslint-disable-next-line
    }, []);

    const updateSelectedView = (view, templateObj, action = undefined) => {
        if (view === 'view-templates') {
            setSearchKey('');
            setFilterOption('All');
            setSelectedCategory(undefined);
            setSearchedTemplates({});
            updateCategoryTemplates(defaultTemplates, 'saveAllCategoryTemplates');
        }
        setSelectedView(view);
    };

    const onSearch = async (first = false, category = selectedCategory, searchParam = searchKey, languages = selectedLanguages) => {
        let currentPage = page;
        if (first) {
            setPage(1);
            currentPage = 1;
            setNodata(false);
            setFetchingCategories(true);
        }
        const payload = {
            includeFlows: true,
        };
        if (searchParam !== '') {
            payload.searchParam = searchParam;
        }

        if (category?.categoryId) {
            payload.categoryId = category?.categoryId
        }
        if (languages?.code) {
            payload.language = languages?.code
        }
        setSelectedView('view-search-template');
        setLoadMore(true);
        let response = await getMasterDocument(payload);
        if (response?.data) {
            const templates = response.data;
            let updatedSearchTemplates = {
                count: response.dataCount,
                templates: first ? templates : [...searchedTemplates?.templates, ...templates],
            };
            setSearchedTemplates(updatedSearchTemplates);
            if (payload.limit * currentPage >= response.dataCount) {
                setNodata(true);
            }
            setPage(currentPage + 1);
        }
        setFetchingCategories(false);
        setLoadMore(false);
    };

    const seeAllByCategory = (category, language) => {
        if (category === 'All' && language === 'All') {
            updateSelectedView('view-templates', null);
        } else {
            setSelectedCategory(category);
            setSelectedLangues(language)
            onSearch(true, category, searchKey, language);
        }
    };

    const onClose = () => {
        setSearchKey('');
        if (!selectedCategory) {
            updateSelectedView('view-templates', null);
        } else {
            onSearch(true, undefined, '');
        }
    };

    const onSelectTemplate = (id, name, template) => {
        setSelectedTemplate(id);      
        setMasterTemplate(template);
        if (selectedTemplate && selectedTemplate === id) {
            setSelectedTemplate('');         
            setMasterTemplate('')
        }
    }

    const renderLoader = (styles = {}) => (
        <LoaderContainer top={styles.top}>
            <Loader />
            <div className="loading-txt">
                {t(
                    DESIGN_AREA_TEMPLATES_TAB.LOADING_MORE_TEMPLATES_TEXT.key,
                    DESIGN_AREA_TEMPLATES_TAB.LOADING_MORE_TEMPLATES_TEXT.value,
                )}
            </div>
        </LoaderContainer>
    );

    const renderSearchBar = () => {
        return (
            <TabSearchContainer
                Master={checkUserAccess('master')}
                isComplex
            >
                <Input
                    name="templateSearch"
                    placeholder={t(
                        DESIGN_AREA_TEMPLATES_TAB.SEARCH_TEMPLATES_PLACEHOLDER.key,
                        DESIGN_AREA_TEMPLATES_TAB.SEARCH_TEMPLATES_PLACEHOLDER.value,
                    )}
                    value={searchKey}
                    onChange={e => setSearchKey(e.target.value)}
                    onPressEnter={() => onSearch(true)}
                    style={{ width: 200 }}
                    prefix={<Icon type="search" />}
                    autoComplete={'off'}
                    suffix={
                        <>
                            {searchKey.length > 0 && <Icon type="close" onClick={onClose} />}
                            <Popover
                                placement="bottomRight"
                                title={t(
                                    DESIGN_AREA_TEMPLATES_TAB.FILTER_POPUP_LABEL.key,
                                    DESIGN_AREA_TEMPLATES_TAB.FILTER_POPUP_LABEL.value,
                                )}
                                trigger="click"
                                visible={filterVisible}
                                onVisibleChange={() => setFilterVisible(!filterVisible)}
                                content={
                                    <PopoverContainer>
                                        <ClearAll
                                            onClick={() => [
                                                setFilterVisible(false),
                                                updateSelectedView('view-templates', null),
                                            ]}
                                        >
                                            {t(
                                                DESIGN_AREA_TEMPLATES_TAB.CLEAR_ALL_FILTER_ACTION.key,
                                                DESIGN_AREA_TEMPLATES_TAB.CLEAR_ALL_FILTER_ACTION.value,
                                            )}
                                        </ClearAll>

                                        <div className='ant-popover-title' style={{ padding: '8px 0px' }}>                                                {t(
                                            DESIGN_AREA_TEMPLATES_TAB.SEARCH_BY_CATEGORY_LABEL.key,
                                            DESIGN_AREA_TEMPLATES_TAB.SEARCH_BY_CATEGORY_LABEL.value,
                                        )}</div>
                                        <Select
                                            loading={fetchingCategories}
                                            showSearch
                                            value={JSON.stringify(filterOption)}
                                            style={{ width: 200 }}
                                            placeholder="Select Category"
                                            filterOption={(input, option) =>
                                                option && option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                            }
                                            onChange={value => setFilterOption(JSON.parse(value))}
                                        >
                                            <Option key="all" value={JSON.stringify('All')}>
                                                {t(
                                                    DESIGN_AREA_TEMPLATES_TAB.ALL_CATEGORY_DROPDOWN_OPTION.key,
                                                    DESIGN_AREA_TEMPLATES_TAB.ALL_CATEGORY_DROPDOWN_OPTION.value,
                                                )}
                                            </Option>
                                            {defaultTemplates.length > 0 &&
                                                defaultTemplates.map(data => (
                                                    <Option key={data.categoryId} value={JSON.stringify(data)}>
                                                        {data.categoryName}
                                                    </Option>
                                                ))}
                                        </Select>

                                        <div className='ant-popover-title title' style={{ padding: '8px 0px' }}>                                     {t(
                                            DESIGN_AREA_TEMPLATES_TAB.SEARCH_BY_LANGUAGE_LABEL.key,
                                            DESIGN_AREA_TEMPLATES_TAB.SEARCH_BY_LANGUAGE_LABEL.value,
                                        )}</div>
                                        <Select
                                            loading={fetchingLanguage}
                                            showSearch
                                            value={JSON.stringify(languageOption)}
                                            style={{ width: 200 }}
                                            placeholder="Select Category"
                                            filterOption={(input, option) =>
                                                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                            }
                                            onChange={value => setLanguageOption(JSON.parse(value))}
                                        >
                                            <Option key="all" value={JSON.stringify('All')}>
                                                {t(
                                                    DESIGN_AREA_TEMPLATES_TAB.ALL_CATEGORY_DROPDOWN_OPTION.key,
                                                    DESIGN_AREA_TEMPLATES_TAB.ALL_CATEGORY_DROPDOWN_OPTION.value,
                                                )}
                                            </Option>
                                            {languageList.length > 0 &&
                                                languageList.map(data => (
                                                    <Option key={data.code} value={JSON.stringify(data)}>
                                                        {data.name}
                                                    </Option>
                                                ))}
                                        </Select>

                                        <Button
                                            type="primary"
                                            onClick={() => [setFilterVisible(false), seeAllByCategory(filterOption, languageOption)]}
                                        >
                                            {t(
                                                DESIGN_AREA_TEMPLATES_TAB.APPLY_FILTERS_BUTTON.key,
                                                DESIGN_AREA_TEMPLATES_TAB.APPLY_FILTERS_BUTTON.value,
                                            )}
                                        </Button>
                                    </PopoverContainer>
                                }
                            >
                                <Badge dot count={selectedCategory ? null : 0}>
                                    <Icon component={FilterSvg} onClick={() => setFilterVisible(!filterVisible)} />
                                </Badge>
                            </Popover>
                        </>
                    }
                />
                {!fetchingCategories && selectedView === 'view-search-template' && (
                    <NoteText>
                        {t(
                            DESIGN_AREA_TEMPLATES_TAB.TEMPLATES_FOUND_COUNT.key,
                            DESIGN_AREA_TEMPLATES_TAB.TEMPLATES_FOUND_COUNT.value,
                            { templatesCount: searchedTemplates?.count },
                        )}
                    </NoteText>
                )}
            </TabSearchContainer>
        );
    };

    const singleTemplate = (templateObj) => { 
        return (

            <TemplateSingleCard
                key={templateObj.id}
                onClick={() => onSelectTemplate(templateObj.id, templateObj.name, templateObj)}
                title={templateObj.name}
                masterTemplate={true}
            >
                <div className="template-media">
                    <div className="image-wrapper">
                        <div style={{ height: '100%' }}>
                            {Object.keys(templateObj).length > 0 &&
                                <div key={templateObj.id} style={{ height: '100%' }}>
                                    <img
                                        src={templateObj['thumbnail'] ? templateObj['thumbnail'] : PageNotFoundIllustration}
                                        alt="pageObj-thumbnail"
                                        className={!templateObj['thumbnail'] ? 'no-thumbnail no-margin' : ''}
                                    />
                                </div>
                            }
                        </div>
                    </div>
                    {selectedTemplate === templateObj.id &&
                        <div className="preview-button1">
                            <div class="wrapper">
                                <div class="circle">
                                    <div class="checkMark"></div>
                                </div>
                            </div>
                        </div>
                    }
                </div>
                <div className='template-name'>
                    <h2>
                        {templateObj.name}
                    </h2>
                </div>

            </TemplateSingleCard >
        );
    };

    const templateCardView = (category, action) => {
        return category.documents && category.documents.length ? (
            <TabMenuItemContainer>
                {category.documents.map(templateObj => {
                    return singleTemplate(templateObj, action);
                })}
            </TabMenuItemContainer>
        ) : (
            <div></div>
        );
    };

    const showMoreView = text => {
        const loaderStyles = { top: '0px' };
        return (
            <>
                {loadMore && renderLoader(loaderStyles)}
                {noData && <NoDataView small>{text}</NoDataView>}
            </>
        );
    };

    const renderSearchView = () => {
        return searchedTemplates.templates?.length > 0 ? (
            <div className='content-wrapper' style={{ height: '100%' }}>
                <TabMenuItemContainer>
                    {searchedTemplates.templates.map(data => {
                        return singleTemplate(data.documentDTO, 'view-search-template');
                    })}
                </TabMenuItemContainer>
                {showMoreView(
                    t(
                        DESIGN_AREA_TEMPLATES_TAB.GOT_ALL_RESULTS.key,
                        DESIGN_AREA_TEMPLATES_TAB.GOT_ALL_RESULTS.value,
                    ),
                )}
            </div>
        ) : (
            <NoDataView>
                {t(
                    DESIGN_AREA_TEMPLATES_TAB.NO_TEMPLATES_AVAILABLE.key,
                    DESIGN_AREA_TEMPLATES_TAB.NO_TEMPLATES_AVAILABLE.value,
                )}
            </NoDataView>
        );
    };

    const tabHeaderView = category => {
        return (

            <Col>
                <TabMenuHeader
                    className='categoryName'>
                    <h2>{category.categoryName}</h2>
                </TabMenuHeader>
                {category.documents && category.documents.length >= 4 && (
                    <TabMenuHeader
                        onClick={() => seeAllByCategory(category)}
                    >
                        <div className="see-all">
                            {t(
                                DESIGN_AREA_TEMPLATES_TAB.SEE_ALL_TEMPLATES_ACTION.key,
                                DESIGN_AREA_TEMPLATES_TAB.SEE_ALL_TEMPLATES_ACTION.value,
                            )}
                        </div>
                    </TabMenuHeader>
                )}
            </Col>
        );
    };

    const renderEmptyView = () => (
        <EmptySettingsDetails >
            <div className="empty-illustration">
                <img src={EmptyElementsIllustration} alt="EmptyElementsIllustration" />
            </div>
            <div className="empty-txt">
                <span>
                    {t(
                        DESIGN_AREA_TEMPLATES_TAB.TEMPLATES_LIST_EMPTY.key,
                        DESIGN_AREA_TEMPLATES_TAB.TEMPLATES_LIST_EMPTY.value,
                    )}
                </span>
            </div>
        </EmptySettingsDetails>
    );

    const renderCategory = () => {
        return (
            <div className='content-wrapper' style={{ height: '100%' }}>
                {categoryTemplates.map(category => (
                    <TabMenuHeaderContainer key={category.id} >
                        {tabHeaderView(category)}
                        {templateCardView(category, 'view-templates')}
                    </TabMenuHeaderContainer>
                ))}
            </div>
        );
    };

    const renderTabBody = () => {
        switch (selectedView) {
            case 'view-templates':
                return renderCategory();
            case 'view-search-template':
                return renderSearchView();
            default:
                break;
        }
    };   
    return (
        <TabMenuContainer style={{ height: '100%' }} >
            {renderSearchBar()}
            {fetchingCategories ? renderLoader() :
                <>
                    {categoryTemplates.length === 0 && renderEmptyView()}
                    {categoryTemplates.length > 0 && renderTabBody()}
                </>}
        </TabMenuContainer>
    );
};

export default withTranslation(NAMESPACES)(MasterTemplate);
